import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './MassSpyPopup.css';

const spyActivities = [
  "Scanning web for business intelligence...",
  "Analyzing website content...",
  "Discovering social profiles...",
  "Extracting email addresses...",
  "Finding LinkedIn profiles...",
  "Locating Instagram accounts...",
  "Scanning Facebook pages...",
  "Gathering phone numbers...",
  "Analyzing business data...",
  "Identifying key personnel...",
  "Cross-referencing sources...",
  "Calculating confidence scores...",
  "Processing AI analysis...",
  "Generating descriptions...",
  "Completing intelligence..."
];

/* ── Live discovery feed helpers ──
   Each completed contact in `massSpyProcessedData` carries a full
   `intelligenceData` payload. We mine it for individual, showable data
   points (emails, decision makers, socials, phones, AI profiles) so the
   feed can fire them in one-by-one — making the raw throughput visible. */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SOCIAL_META = {
  linkedin: { icon: 'fa-brands fa-linkedin-in', label: 'LinkedIn' },
  instagram: { icon: 'fa-brands fa-instagram', label: 'Instagram' },
  facebook: { icon: 'fa-brands fa-facebook-f', label: 'Facebook' },
  tiktok: { icon: 'fa-brands fa-tiktok', label: 'TikTok' },
  twitter: { icon: 'fa-brands fa-x-twitter', label: 'X' },
  youtube: { icon: 'fa-brands fa-youtube', label: 'YouTube' },
};

const DISCOVERY_META = {
  email: { icon: 'fa-solid fa-envelope', tag: 'Email' },
  dm: { icon: 'fa-solid fa-user-tie', tag: 'Decision Maker' },
  social: { icon: 'fa-solid fa-share-nodes', tag: 'Social' },
  phone: { icon: 'fa-solid fa-phone', tag: 'Phone' },
  desc: { icon: 'fa-solid fa-file-lines', tag: 'AI Profile' },
};

// Slot-machine style juice: little symbols that occasionally pop + rise off the
// captured-count number to make each new data point feel like a small win.
const POP_SYMBOLS = ['⚡', '💎', '🔥', '🚀', '✨'];

/* Scripted warm-up feed — shown in the first seconds before the real results
   land, so the user never stares at an empty box. Each line is hardcoded but
   stitched with live data (contact count + the actual first/second company
   names) so it reads as a genuine boot sequence, not filler. */
function buildWarmupFeed(total, contacts) {
  if (!total || total <= 0) return [];
  const names = (contacts || [])
    .map(c => c?.business_name || c?.lead_website)
    .filter(Boolean);
  const first = names[0];
  const second = names[1];
  return [
    { type: 'system', accent: 'violet', icon: 'fa-solid fa-microchip', tag: 'AI', value: 'BonusClub AI engine online', company: 'System ready' },
    { type: 'system', accent: 'blue', icon: 'fa-solid fa-database', tag: 'Pipeline', value: `${total} contact${total === 1 ? '' : 's'} loaded into pipeline`, company: 'Queue built' },
    { type: 'system', accent: 'cyan', icon: 'fa-solid fa-globe', tag: 'Crawler', value: 'Spinning up web crawlers', company: 'Multi-source' },
    { type: 'system', accent: 'green', icon: 'fa-solid fa-plug', tag: 'Sources', value: 'Connecting live data sources', company: 'Realtime' },
    first && { type: 'system', accent: 'rose', icon: 'fa-solid fa-crosshairs', tag: 'Target', value: `Locking on ${first}`, company: 'First target' },
    { type: 'system', accent: 'indigo', icon: 'fa-solid fa-shuffle', tag: 'AI', value: 'Cross-referencing intelligence sources', company: 'Verifying' },
    { type: 'system', accent: 'amber', icon: 'fa-solid fa-user-tie', tag: 'Module', value: 'Decision-maker module armed', company: 'Ready' },
    second && { type: 'system', accent: 'teal', icon: 'fa-solid fa-magnifying-glass', tag: 'Scan', value: `Queuing ${second}`, company: 'Up next' },
    { type: 'system', accent: 'orange', icon: 'fa-solid fa-bolt', tag: 'Live', value: 'First signals incoming…', company: 'Stand by' },
  ].filter(Boolean);
}

function collectFeedEmails(intel) {
  const out = [];
  const push = (e) => { const v = typeof e === 'string' ? e.trim() : ''; if (v && EMAIL_RE.test(v)) out.push(v.toLowerCase()); };
  (intel.emailsFoundInSearch || []).forEach(push);
  (intel.openAIAnalysis?.emails || []).forEach(push);
  (intel.aiContentAnalysis?.contactSummary?.emails || []).forEach(item => push(typeof item === 'string' ? item : item?.email));
  return [...new Set(out)];
}

function collectFeedSocials(intel) {
  const sources = [
    intel.socialProfiles || {},
    intel.aiAnalysis?.socialMediaLinks || {},
    intel.aiContentAnalysis?.socialMediaProfiles || {},
    intel.scrapingResults?.data?.socialLinks || {},
  ];
  const found = [];
  for (const plat of Object.keys(SOCIAL_META)) {
    const hit = sources.some(s => { const v = s[plat]; return v && typeof v === 'string' && v !== 'null'; });
    if (hit) found.push(plat);
  }
  if (intel.linkedinIntelligence?.companyProfile?.url && !found.includes('linkedin')) found.push('linkedin');
  return found;
}

function collectFeedPhone(intel) {
  const c = intel.phoneNumber || intel.openAIAnalysis?.phone;
  return typeof c === 'string' && c.trim() ? c.trim() : null;
}

/** Flatten one processed contact into an ordered list of feed discoveries. */
function buildDiscoveries(item) {
  const intel = item?.intelligenceData;
  if (!item || item.status !== 'success' || !intel) return [];
  const company = item.originalContact?.business_name || item.originalContact?.lead_website || 'Unknown';
  const out = [];
  collectFeedEmails(intel).slice(0, 3).forEach(email => out.push({ type: 'email', company, value: email }));
  (intel.decisionMakers || [])
    .filter(dm => dm && (dm.name || dm.role))
    .slice(0, 4)
    .forEach(dm => out.push({ type: 'dm', company, value: dm.name || 'Decision Maker', sub: dm.role || null }));
  collectFeedSocials(intel).forEach(plat => out.push({ type: 'social', company, platform: plat, value: SOCIAL_META[plat].label }));
  const phone = collectFeedPhone(intel);
  if (phone) out.push({ type: 'phone', company, value: phone });
  if (item.hasDescription) out.push({ type: 'desc', company, value: 'AI business profile written' });
  return out;
}

const MassSpyPopup = ({
  isMassSpyActive,
  massSpyProgress,
  massSpyTotal,
  massSpyQueue,
  massSpyContacts,
  massSpyResults,
  massSpyProcessedData,
  showMassSpyReview,
  activeModules,
  // Smart Funnel one-click run: when true, the review is applied automatically
  // as soon as it appears (no manual "Apply All Intelligence" click).
  autoConfirmReview = false,
  closeMassSpy,
  onDeleteNoEmailEntries,
  onReviewConfirm,
  onReviewCancel
}) => {
  const [currentActivity, setCurrentActivity] = useState(spyActivities[0]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [showFailedDetails, setShowFailedDetails] = useState(false);
  const [revealedCount, setRevealedCount] = useState(0);
  const [pops, setPops] = useState([]);
  const [fakePercent, setFakePercent] = useState(0);
  const mountedRef = useRef(true);
  const feedStartRef = useRef(null);
  const popIdRef = useRef(0);
  const prevRevealedRef = useRef(0);
  const sliceStartRef = useRef(Date.now());

  // Scripted boot sequence shown until the first real results arrive.
  const warmupItems = useMemo(() => buildWarmupFeed(massSpyTotal, massSpyContacts), [massSpyTotal, massSpyContacts]);

  // Warm-up lines first, then every completed contact flattened into individual
  // showable data points. Both flow through the same reveal pipeline.
  const allDiscoveries = useMemo(() => {
    const list = warmupItems.map((w, i) => ({ ...w, id: `w-${i}` }));
    (massSpyProcessedData || []).forEach((item, pi) => {
      buildDiscoveries(item).forEach((d, di) => list.push({ ...d, id: `${pi}-${di}` }));
    });
    return list;
  }, [warmupItems, massSpyProcessedData]);

  // The headline counter + velocity + milestones track REAL data only, so the
  // warm-up lines animate in but don't inflate the "data points captured" stat.
  const warmupCount = warmupItems.length;
  const capturedCount = Math.max(0, revealedCount - warmupCount);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);
  
  useEffect(() => {
    if (isCompleted) return;
    const interval = setInterval(() => {
      setCurrentActivity(spyActivities[Math.floor(Math.random() * spyActivities.length)]);
    }, 2800);
    return () => clearInterval(interval);
  }, [isCompleted]);

  useEffect(() => {
    if (massSpyProgress === massSpyTotal && massSpyTotal > 0 && massSpyResults) {
      setIsCompleted(true);
    } else {
      setIsCompleted(false);
    }
  }, [massSpyProgress, massSpyTotal, massSpyResults]);

  // Restart the feed cleanly at the start of every run (progress back to 0) and
  // clear it when idle — covers re-opening the modal for a second batch too.
  useEffect(() => {
    if (massSpyProgress === 0) {
      setRevealedCount(0);
      setPops([]);
      setFakePercent(0);
      prevRevealedRef.current = 0;
      feedStartRef.current = isMassSpyActive ? Date.now() : null;
      // The component stays mounted across runs, so reset the slice timer here
      // too — otherwise the fake fill uses an ancient timestamp and jumps
      // straight to the ceiling instead of creeping up.
      sliceStartRef.current = Date.now();
    }
  }, [massSpyProgress, isMassSpyActive]);

  // Fake-but-honest progress: a single contact can take a while, so a real
  // bar would sit at 0% for ages. We creep the bar up within the CURRENT
  // contact's slice using a decelerating curve — it fills most of the slice
  // quickly, then crawls, and never claims the slice is finished until the
  // real progress catches up. Restarts the slice timer every real step.
  useEffect(() => {
    sliceStartRef.current = Date.now();
  }, [massSpyProgress]);

  useEffect(() => {
    if (isCompleted || !isMassSpyActive || !massSpyTotal) { setFakePercent(0); return; }
    const realPct = (massSpyProgress / massSpyTotal) * 100;
    const sliceSpan = (1 / massSpyTotal) * 100;       // % weight of one contact
    const ceiling = realPct + sliceSpan * 0.9;        // never fully complete the slice
    const tau = 6500;                                  // higher = slower crawl
    const tick = () => {
      const elapsed = Date.now() - sliceStartRef.current;
      const crept = realPct + (ceiling - realPct) * (1 - Math.exp(-elapsed / tau));
      setFakePercent(Math.min(ceiling, crept));
    };
    tick();
    const interval = setInterval(tick, 120);
    return () => clearInterval(interval);
  }, [massSpyProgress, massSpyTotal, isCompleted, isMassSpyActive]);

  // Cascade the discoveries in one-by-one. We want an organic trickle — info
  // dripping in step by step — instead of "quiet … burst … quiet". Each reveal
  // waits a gentle, slightly randomized beat; when a backlog builds we ease the
  // pace up a touch (but never machine-gun), so the stream keeps flowing evenly.
  useEffect(() => {
    if (revealedCount >= allDiscoveries.length) return;
    const nextIsWarmup = revealedCount < warmupCount;
    const realArrived = allDiscoveries.length > warmupCount;
    let delay;
    if (nextIsWarmup && !realArrived) {
      // Stretch the scripted boot lines across ~13s so the wait before the first
      // real results never feels empty. Once real data lands we drop back to the
      // normal organic pace below so it isn't held up.
      delay = 13000 / Math.max(1, warmupCount) + (Math.random() * 260 - 130);
    } else {
      const pending = allDiscoveries.length - revealedCount;
      const base = pending > 14 ? 190 : pending > 7 ? 270 : 400;
      delay = base + Math.random() * 180;
    }
    const t = setTimeout(() => setRevealedCount(c => Math.min(c + 1, allDiscoveries.length)), Math.max(60, delay));
    return () => clearTimeout(t);
  }, [revealedCount, allDiscoveries.length, warmupCount]);

  // Spawn particles only when a milestone is hit (every 10 data points) — keeps
  // it a rare, earned reward. Each hit fires a TikTok-style burst: usually a
  // single emoji, but sometimes a quick staggered cascade of a few for that
  // juicy "it's really cooking" moment (kept tasteful for a business context).
  useEffect(() => {
    const prev = prevRevealedRef.current;
    prevRevealedRef.current = capturedCount;
    if (capturedCount <= 0 || capturedCount <= prev) return;
    const crossedMilestone = Math.floor(capturedCount / 10) > Math.floor(prev / 10);
    if (!crossedMilestone) return;

    const r = Math.random();
    const burst = r > 0.85 ? 4 : r > 0.6 ? 3 : r > 0.3 ? 2 : 1;
    const timers = [];
    for (let i = 0; i < burst; i++) {
      timers.push(setTimeout(() => {
        const id = ++popIdRef.current;
        const pop = {
          id,
          emoji: POP_SYMBOLS[Math.floor(Math.random() * POP_SYMBOLS.length)],
          left: 2 + Math.random() * 70,
          dx: Math.random() * 34 - 17,
          rot: Math.random() * 50 - 25,
          // Most are normal, every so often a big "hero" one for extra pop.
          size: Math.random() > 0.72 ? 27 + Math.random() * 9 : 16 + Math.random() * 5,
        };
        setPops(p => [...p, pop]);
        timers.push(setTimeout(() => setPops(p => p.filter(x => x.id !== id)), 1250));
      }, i * (90 + Math.random() * 80)));
    }
    return () => timers.forEach(clearTimeout);
  }, [capturedCount]);

  const handleDeleteNoEmailEntries = () => {
    if (!onDeleteNoEmailEntries || isDeleting) return;
    setIsDeleting(true);
    onDeleteNoEmailEntries();
    setTimeout(() => setIsDeleting(false), 2000);
  };

  const handleApply = async () => {
    if (isApplying) return;
    setIsApplying(true);
    // UI-level safety net: even if onReviewConfirm somehow never resolves
    // (e.g. a stalled Firestore write that never gets a server ack), the user
    // must never be trapped on an endless "Applying…" spinner. After a hard
    // cap we force the spinner off and close the modal — the data was already
    // applied optimistically and reloads on next open anyway.
    const watchdog = setTimeout(() => {
      if (!mountedRef.current) return;
      console.warn('[MASS-SPY-APPLY] Apply watchdog fired — forcing modal closed.');
      setIsApplying(false);
      closeMassSpy();
    }, 22000);
    try {
      await onReviewConfirm();
    } finally {
      clearTimeout(watchdog);
      if (mountedRef.current) setIsApplying(false);
    }
  };

  // Smart Funnel one-click run: auto-apply the review the moment it appears.
  // handleApply's own `isApplying` guard prevents a double fire (and blocks a
  // stray manual click during the apply). The normal combo / plain research
  // leave autoConfirmReview false, so they still wait for the manual click.
  useEffect(() => {
    if (showMassSpyReview && autoConfirmReview && !isApplying) {
      handleApply();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMassSpyReview, autoConfirmReview]);

  if (!isMassSpyActive && !showMassSpyReview) return null;

  const realPercent = Math.round((massSpyProgress / massSpyTotal) * 100);
  // Show whichever is higher: real progress, or the creeping fake fill. This
  // keeps the bar alive in the first seconds without ever lying about a
  // contact being fully done (the fake fill caps just below the next step).
  const progressPercent = isCompleted ? realPercent : Math.max(realPercent, Math.round(fakePercent));
  const currentIdx = Math.min(massSpyProgress, (massSpyContacts || []).length - 1);
  const currentContact = (massSpyContacts || [])[currentIdx >= 0 ? currentIdx : 0];
  const currentTarget = currentContact?.business_name || currentContact?.lead_website || "...";
  const entriesWithoutEmail = massSpyTotal - massSpyResults.emailsFound;
  const hasEntriesWithoutEmail = entriesWithoutEmail > 0;
  const dmEnabled = (activeModules || []).includes('decisionMakers');
  const dmFound = massSpyResults.decisionMakersFound || 0;

  // Live feed: only show what's been "revealed" so far, newest first, capped.
  const isProcessing = !isCompleted && !showMassSpyReview;
  const feed = allDiscoveries.slice(0, revealedCount).slice(-40).reverse();
  // Once the scripted boot lines are all shown but no real data has surfaced
  // yet, keep a live loading banner on top so the feed is never frozen.
  const showFeedLoading = isProcessing && warmupCount > 0 && revealedCount >= warmupCount && allDiscoveries.length <= warmupCount;
  const feedElapsedSec = feedStartRef.current ? Math.max(1, (Date.now() - feedStartRef.current) / 1000) : 1;
  const feedSpeed = (capturedCount / feedElapsedSec).toFixed(1);

  // On <body> so the blur covers the app header. Nested in the Targets pane
  // the overlay sits in a lower stacking context than the fixed nav.
  return createPortal(
    <div className="mass-spy-overlay-wrapper">
      <div className="mass-spy-overlay-bg"></div>
      <div className={`mass-spy-popup-modal${isProcessing ? ' mass-spy-popup-modal--cockpit' : ''}${(isCompleted || showMassSpyReview) ? ' mass-spy-popup-modal--complete' : ''}`}>
        <div className="msp-orbs" aria-hidden="true">
          <span className="msp-orb msp-orb--1"></span>
          <span className="msp-orb msp-orb--2"></span>
          <span className="msp-orb msp-orb--3"></span>
        </div>
        {(isCompleted || showMassSpyReview) && <div className="msp-silver-sweep" aria-hidden="true"></div>}
        <div className="mass-spy-popup-content">

          {/* Header */}
          <div className="msp-header">
            <div className="msp-header-left">
              <h2><span className="msp-brand-bold">SalesGod</span> <span className="msp-brand-light">Research</span></h2>
              <span className="msp-header-subtitle">
                {isCompleted ? 'Research completed' : `Analyzing ${massSpyTotal} contacts...`}
              </span>
            </div>
            <button className="msp-close-x" onClick={closeMassSpy}>×</button>
          </div>

          {/* Processing State */}
          {!isCompleted && !showMassSpyReview && (
            <div className="msp-cockpit">

              {/* ── Left: power core ── */}
              <div className="msp-cockpit-core">
                <div className="msp-processing-area">
                  <div className="msp-orbit-container">
                    <div className="msp-orbit-ring"></div>
                    <div className="msp-orbit-center">
                      <img src="/img/salesgod_dark.webp" alt="Research" className="msp-center-icon" />
                    </div>
                  </div>

                  <div className="msp-counter">
                    <span className="msp-counter-current">{massSpyProgress}</span>
                    <span className="msp-counter-sep">/</span>
                    <span className="msp-counter-total">{massSpyTotal}</span>
                  </div>
                </div>

                <div className="msp-target-card">
                  <div className="msp-target-label">RESEARCHING</div>
                  <div className="msp-target-name">{currentTarget}</div>
                  <div className="msp-target-activity">
                    <span className="msp-activity-dot"></span>
                    {currentActivity}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="msp-progress-container">
                  <div className="msp-progress-bar">
                    <div className="msp-progress-fill" style={{width: `${progressPercent}%`}}></div>
                  </div>
                  <div className="msp-progress-text">
                    <span className="msp-progress-count">{massSpyProgress} of {massSpyTotal} contacts</span>
                    <span className="msp-progress-percent">{progressPercent}%</span>
                  </div>
                </div>

                {/* Live Stats */}
                {massSpyProgress > 0 && (
                  <div className="msp-live-stats">
                    <div className="msp-live-stat">
                      <span className="msp-live-stat-value">{massSpyResults.emailsFound}</span>
                      <span className="msp-live-stat-label">Emails</span>
                    </div>
                    <div className="msp-live-stat">
                      <span className="msp-live-stat-value">{massSpyResults.socialProfilesFound || 0}</span>
                      <span className="msp-live-stat-label">Socials</span>
                    </div>
                    <div className="msp-live-stat">
                      <span className="msp-live-stat-value">{massSpyResults.successCount}</span>
                      <span className="msp-live-stat-label">Done</span>
                    </div>
                    {dmEnabled && dmFound > 0 && (
                      <div className="msp-live-stat msp-live-stat--dm">
                        <span className="msp-live-stat-value">{dmFound}</span>
                        <span className="msp-live-stat-label">Decision Makers</span>
                      </div>
                    )}
                    {massSpyResults.errorCount > 0 && (
                      <div className="msp-live-stat msp-live-stat--error">
                        <span className="msp-live-stat-value">{massSpyResults.errorCount}</span>
                        <span className="msp-live-stat-label">Failed</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── Right: live intelligence feed ── */}
              <div className="msp-cockpit-feed">
                <div className="msp-feed-head">
                  <span className="msp-feed-live">
                    <span className="msp-feed-live-dot"></span>
                    BonusClub AI
                  </span>
                  <span className="msp-feed-velocity">
                    <i className="fa-solid fa-bolt"></i>
                    {feedSpeed}<span className="msp-feed-velocity-unit">/s</span>
                  </span>
                </div>

                <div className="msp-feed-capture">
                  <span className="msp-feed-capture-numwrap">
                    <span key={capturedCount} className="msp-feed-capture-num">{capturedCount}</span>
                    <span className="msp-feed-pops" aria-hidden="true">
                      {pops.map(p => (
                        <span
                          key={p.id}
                          className="msp-pop"
                          style={{ left: `${p.left}%`, '--pop-dx': `${p.dx}px`, '--pop-rot': `${p.rot}deg`, '--pop-size': `${p.size}px` }}
                        >{p.emoji}</span>
                      ))}
                    </span>
                  </span>
                  <span className="msp-feed-capture-label">data points captured</span>
                </div>

                <div className="msp-feed-stream">
                  {showFeedLoading && (
                    <div className="msp-feed-loading">
                      <span className="msp-feed-loading-spinner"></span>
                      <div className="msp-feed-loading-body">
                        <span className="msp-feed-loading-title">Harvesting live data…</span>
                        <span className="msp-feed-loading-sub">{currentActivity}</span>
                      </div>
                      <span className="msp-feed-loading-dots"><i></i><i></i><i></i></span>
                    </div>
                  )}
                  {feed.length === 0 ? (
                    <div className="msp-feed-empty">
                      <span className="msp-feed-empty-pulse"></span>
                      Listening for signals...
                    </div>
                  ) : (
                    feed.map(d => {
                      const meta = d.type === 'system'
                        ? { icon: d.icon, tag: d.tag }
                        : d.type === 'social'
                          ? { icon: SOCIAL_META[d.platform]?.icon || DISCOVERY_META.social.icon, tag: SOCIAL_META[d.platform]?.label || 'Social' }
                          : DISCOVERY_META[d.type];
                      return (
                        <div key={d.id} className={`msp-feed-row msp-feed-row--${d.type}${d.accent ? ` msp-sys--${d.accent}` : ''}`}>
                          <span className="msp-feed-row-icon"><i className={meta.icon}></i></span>
                          <div className="msp-feed-row-body">
                            <span className="msp-feed-row-value">{d.value}</span>
                            <span className="msp-feed-row-meta">
                              {d.sub ? <span className="msp-feed-row-sub">{d.sub} · </span> : null}
                              <span className="msp-feed-row-company">{d.company}</span>
                            </span>
                          </div>
                          <span className="msp-feed-row-tag">{meta.tag}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Review Mode / Completed */}
          {(showMassSpyReview || (isCompleted && !showMassSpyReview)) && (
            <>
              {/* Hero Banner */}
              <div className="msp-result-hero">
                <div className="msp-result-hero-bg"></div>
                <div className="msp-result-hero-content">
                  <div className="msp-result-hero-icon-wrap">
                    <img src="/img/salesgod_dark.webp" alt="SalesGod" className="msp-result-hero-icon" />
                    <span className="msp-result-hero-check">
                      <i className="fa-solid fa-check"></i>
                    </span>
                  </div>
                  <div className="msp-result-hero-text">
                    <span className="msp-result-hero-title">Research Complete</span>
                    <span className="msp-result-hero-sub">
                      <strong>{massSpyResults.successCount}</strong> of {massSpyTotal} contacts analyzed
                      {massSpyResults.errorCount > 0 && <span className="msp-result-hero-errors"> · {massSpyResults.errorCount} failed</span>}
                    </span>
                  </div>
                </div>
              </div>

              {/* Icon Stats */}
              <div className="msp-result-stats">
                <div className="msp-result-stat">
                  <div className="msp-result-stat-icon msp-result-stat-icon--email">
                    <i className="fa-solid fa-envelope"></i>
                  </div>
                  <div className="msp-result-stat-info">
                    <span className="msp-result-stat-value">{massSpyResults.emailsFound}</span>
                    <span className="msp-result-stat-label">Emails Found</span>
                  </div>
                </div>
                <div className="msp-result-stat">
                  <div className="msp-result-stat-icon msp-result-stat-icon--social">
                    <i className="fa-solid fa-share-nodes"></i>
                  </div>
                  <div className="msp-result-stat-info">
                    <span className="msp-result-stat-value">{massSpyResults.socialProfilesFound || 0}</span>
                    <span className="msp-result-stat-label">Social Profiles</span>
                  </div>
                </div>
                <div className="msp-result-stat">
                  <div className="msp-result-stat-icon msp-result-stat-icon--desc">
                    <i className="fa-solid fa-file-lines"></i>
                  </div>
                  <div className="msp-result-stat-info">
                    <span className="msp-result-stat-value">{massSpyResults.descriptionsFound || 0}</span>
                    <span className="msp-result-stat-label">Descriptions</span>
                  </div>
                </div>
                {massSpyResults.highConfidenceCount > 0 && (
                  <div className="msp-result-stat">
                    <div className="msp-result-stat-icon msp-result-stat-icon--conf">
                      <i className="fa-solid fa-shield-halved"></i>
                    </div>
                    <div className="msp-result-stat-info">
                      <span className="msp-result-stat-value">{massSpyResults.highConfidenceCount}</span>
                      <span className="msp-result-stat-label">High Confidence</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Decision Makers Banner */}
              {dmEnabled && dmFound > 0 && (
                <div className="msp-dm-banner">
                  <div className="msp-dm-banner-icon">
                    <i className="fa-solid fa-user-tie"></i>
                  </div>
                  <div className="msp-dm-banner-content">
                    <span className="msp-dm-banner-title">
                      <strong>{dmFound}</strong> Decision Maker{dmFound !== 1 ? 's' : ''} identified
                    </span>
                    <span className="msp-dm-banner-sub">Key executives and contacts found across your researched companies</span>
                  </div>
                </div>
              )}

              {/* Insights */}
              {(massSpyResults.emailsFound > 0 || massSpyResults.descriptionsFound > 0) && (
                <div className="msp-result-insights">
                  {massSpyResults.emailsFound > 0 && (
                    <div className="msp-result-insight">
                      <span className="msp-result-insight-dot msp-result-insight-dot--green"></span>
                      <strong>{massSpyResults.emailsFound}</strong> new email addresses ready for outreach
                    </div>
                  )}
                  {massSpyResults.descriptionsFound > 0 && (
                    <div className="msp-result-insight">
                      <span className="msp-result-insight-dot msp-result-insight-dot--blue"></span>
                      <strong>{massSpyResults.descriptionsFound}</strong> AI-generated business descriptions
                    </div>
                  )}
                  {massSpyResults.socialProfilesFound > 0 && (
                    <div className="msp-result-insight">
                      <span className="msp-result-insight-dot msp-result-insight-dot--purple"></span>
                      <strong>{massSpyResults.socialProfilesFound}</strong> social profiles discovered
                    </div>
                  )}
                </div>
              )}

              {/* Failed Details Toggle */}
              {massSpyResults.errorCount > 0 && (() => {
                const failedEntries = (massSpyProcessedData || []).filter(d => d.status === 'failed');
                return (
                  <div className="msp-failed-section">
                    <button className="msp-failed-toggle" onClick={() => setShowFailedDetails(p => !p)}>
                      <span className="msp-failed-toggle-left">
                        <i className="fa-solid fa-circle-exclamation" />
                        <span>{massSpyResults.errorCount} failed contact{massSpyResults.errorCount !== 1 ? 's' : ''}</span>
                      </span>
                      <i className={`fa-solid fa-chevron-down msp-failed-chevron ${showFailedDetails ? 'msp-failed-chevron--open' : ''}`} />
                    </button>
                    {showFailedDetails && (
                      <div className="msp-failed-list">
                        {failedEntries.map((entry, i) => (
                          <div className="msp-failed-row" key={i}>
                            <div className="msp-failed-row-name">
                              {entry.originalContact?.business_name || entry.originalContact?.lead_website || `Contact ${i + 1}`}
                            </div>
                            <div className="msp-failed-row-error">{entry.error || 'Unknown error'}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Actions */}
              <div className="msp-result-actions">
                {showMassSpyReview ? (
                  <>
                    <button className="msp-result-apply-btn" onClick={handleApply} disabled={isApplying}>
                      {isApplying 
                        ? <><i className="fa-solid fa-spinner fa-spin"></i> Applying...</>
                        : <><i className="fa-solid fa-check"></i> Apply All Intelligence</>
                      }
                    </button>
                    <button className="msp-result-discard-btn" onClick={onReviewCancel}>
                      Discard
                    </button>
                  </>
                ) : (
                  <button className="msp-result-apply-btn" onClick={closeMassSpy}>
                    Done
                  </button>
                )}
              </div>
            </>
          )}

          <div className="msp-disclaimer">
            <i className="fa fa-exclamation-triangle" aria-hidden />
            <span>Research results are AI-generated and may contain errors. Please verify all data before using it in your outreach.</span>
          </div>

        </div>
      </div>
    </div>,
    document.body
  );
};

export default MassSpyPopup;
